import React from 'react';
import './App.css';
import './styles/rtl.css';
import './styles/animations.css';
import './styles/themes-unified.css';
import './styles/chart-fix.css';
import './styles/custom-dashboard.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { TimePeriod } from './types/api';
import { AuthProvider } from './contexts/AuthContext';
import { BranchProvider } from './contexts/BranchContext';
import { ChartGuideProvider } from './contexts/ChartGuideContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MagicTokenHandler from './components/auth/MagicTokenHandler';
import EnvHandoffHandler from './components/auth/EnvHandoffHandler';
import { setupChartDefaults } from './config/chartSetup';

// Dashboard components
import SmartEvaluationReportExactMatch from './dashboards/SmartEvaluationReportExactMatch';
import EvaluationGrowthDashboard from './dashboards/EvaluationGrowthDashboard';
import SatisfactionDashboard from './dashboards/SatisfactionDashboard';
import SubscriptionDashboard from './dashboards/SubscriptionDashboard';
import BenchmarkDashboard from './dashboards/BenchmarkDashboard';
import MarketPulseDashboard from './dashboards/MarketPulseDashboard';
import ClubDashboard from './dashboards/ClubDashboard';
import ClubConditionsDashboard from './dashboards/ClubConditionsDashboard';
import CustomerClubHeatmapDashboard from './dashboards/CustomerClubHeatmapDashboard';
import HRDashboard from './dashboards/HRDashboard';
import AppLayout from './components/AppLayout';
import DeviceAwareLayout from './components/DeviceAwareLayout';
import { ThemeProvider } from './contexts/ThemeContext';
import { TimeFrameProvider } from './contexts/TimeFrameContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
// Import dashboard components
import MainPerformanceDashboard from './dashboards/MainPerformanceDashboard';
import ExecutiveDashboard from './dashboards/ExecutiveDashboard';
import CustomerAnalyticsDashboard from './dashboards/CustomerAnalyticsDashboard';
import CustomerGroupingDashboard from './dashboards/CustomerGroupingDashboard';
import CustomerPurchaseBehaviorDashboard from './dashboards/CustomerPurchaseBehaviorDashboard';
import RefineCustomerDashboard from './dashboards/RefineCustomerDashboard';
import TechnicalShowcaseDashboard from './dashboards/TechnicalShowcaseDashboard';
import ProductDocumentDashboard from './dashboards/ProductDocumentDashboard';
import ChangelogDashboard from './dashboards/ChangelogDashboard';
import MarketIndicatorsDashboard from './dashboards/MarketIndicatorsDashboard';
import SalesDashboard from './dashboards/SalesDashboard';
import TailwindCustomDashboard from './dashboards/TailwindCustomDashboard';
import BrandOverviewDashboard from './dashboards/BrandOverviewDashboard';
import BranchComparisonDashboard from './dashboards/BranchComparisonDashboard';
import BranchSummaryDashboard from './dashboards/BranchSummaryDashboard';
import PresentationDashboard from './dashboards/PresentationDashboard';
import ConsultationRequest from './components/ConsultationRequest';
import GoftinoWrapper from './components/GoftinoWrapper';
import ConsultationModal from './components/ConsultationModal';
import DemoLoginPage from './pages/DemoLoginPage';
import { useConsultationPrompt } from './hooks/useConsultationPrompt';

// Setup Chart.js global defaults for better readability
setupChartDefaults();

// Root redirect component that preserves query parameters
const RootRedirect: React.FC = () => {
  const location = window.location;
  const searchParams = location.search;
  return <Navigate to={`/dashboard/main${searchParams}`} replace />;
};

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Utility function to convert UI timeFrame to API timeFrame format
const convertToApiTimeFrame = (uiTimeFrame: string): TimePeriod => {
  switch (uiTimeFrame) {
    case 'last1Month': return '1month';
    case 'last3Months': return '3months';
    case 'last6Months': return '6months';
    case 'lastYear': return '12months';
    default: return '6months';
  }
};

// Dashboard wrapper with all providers
const DashboardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isOpen, onClose } = useConsultationPrompt();

  return (
    <AuthProvider>
      <BranchProvider>
        <GoftinoWrapper>
          <div dir="rtl">
            <ThemeProvider>
              <ChartGuideProvider>
                <TimeFrameProvider>
                  <FavoritesProvider>
                    <ProtectedRoute>
                      {children}
                      <ConsultationModal isOpen={isOpen} onClose={onClose} />
                    </ProtectedRoute>
                  </FavoritesProvider>
                </TimeFrameProvider>
              </ChartGuideProvider>
            </ThemeProvider>
          </div>
        </GoftinoWrapper>
      </BranchProvider>
    </AuthProvider>
  );
};

// Dashboard renderer component
const DashboardRenderer: React.FC<{ dashboardId: string }> = ({ dashboardId }) => {
  return (
    <DeviceAwareLayout>
      {({ selectedBrand, selectedBranch, selectedPeriod }) => {
        // Render the appropriate dashboard based on the route
        switch(dashboardId) {
          case 'main':
            return (
              <div className="animate-enter-up">
                <MainPerformanceDashboard
                  brandId={selectedBrand?.id} 
                  branchId={selectedBranch?.id} 
                  timeRange={selectedPeriod}
                />
              </div>
            );
          case 'evaluation':
            return (
              <div className="animate-enter-right stagger-2">
                <SmartEvaluationReportExactMatch 
                  selectedBrand={selectedBrand} 
                  selectedBranch={selectedBranch} 
                />
              </div>
            );
          case 'evaluation-growth':
            return (
              <div className="animate-enter-right stagger-2">
                <EvaluationGrowthDashboard
                  selectedBrand={selectedBrand}
                  selectedBranch={selectedBranch}
                />
              </div>
            );
          case 'hr':
            return (
              <div className="animate-enter-left stagger-5">
                <HRDashboard
                  selectedBrand={selectedBrand} 
                  selectedBranch={selectedBranch} 
                  selectedPeriod={selectedPeriod}
                />
              </div>
            );
          case 'satisfaction':
            return (
              <div className="animate-scale-in stagger-3">
                <SatisfactionDashboard 
                  branchId={selectedBranch?.id} 
                  timePeriod={selectedPeriod}
                />
              </div>
            );
          case 'subscription':
            return (
              <div className="animate-scale-in stagger-3">
                <SubscriptionDashboard
                  branchId={selectedBranch?.id}
                  timePeriod={selectedPeriod}
                />
              </div>
            );
          case 'market-pulse':
            return (
              <div className="animate-enter-up stagger-4">
                <MarketPulseDashboard
                  branchId={selectedBranch?.id}
                  currency={selectedBranch?.currency}
                  customTimeRange={convertToApiTimeFrame(selectedPeriod)}
                />
              </div>
            );
          case 'benchmark':
            return (
              <div className="animate-enter-up stagger-4">
                <BenchmarkDashboard view="annual" />
              </div>
            );
          case 'benchmark-indicators':
            return (
              <div className="animate-enter-up stagger-4">
                <BenchmarkDashboard view="indicators" />
              </div>
            );
          case 'benchmark-monthly':
            return (
              <div className="animate-enter-up stagger-4">
                <BenchmarkDashboard view="monthly" />
              </div>
            );
          case 'market-indicators':
            return (
              <div className="animate-enter-up stagger-4">
                <MarketIndicatorsDashboard />
              </div>
            );
          case 'sales':
            return (
              <div className="animate-enter-up stagger-4">
                <SalesDashboard
                  branchId={selectedBranch?.id}
                  timeRange={selectedPeriod}
                  apiTimeRange={convertToApiTimeFrame(selectedPeriod)}
                />
              </div>
            );
          case 'club':
            return (
              <div className="animate-slide-in-right stagger-6">
                <ClubDashboard 
                  branchId={selectedBranch?.id} 
                  currency={selectedBranch?.currency} 
                  customTimeRange={convertToApiTimeFrame(selectedPeriod)}
                />
              </div>
            );
          case 'club-conditions':
            return (
              <div className="animate-slide-in-right stagger-6">
                <ClubConditionsDashboard 
                  branchId={selectedBranch?.id} 
                  customTimeRange={convertToApiTimeFrame(selectedPeriod)}
                />
              </div>
            );
          case 'club-heatmap':
            return (
              <div className="animate-slide-in-left stagger-7">
                <CustomerClubHeatmapDashboard 
                  selectedBrand={selectedBrand} 
                  selectedBranch={selectedBranch}
                  selectedPeriod={convertToApiTimeFrame(selectedPeriod)}
                />
              </div>
            );
          case 'executive':
            return (
              <div className="animate-enter-up stagger-8">
                <ExecutiveDashboard 
                  selectedBrand={selectedBrand} 
                  selectedBranch={selectedBranch}
                  currency={selectedBranch?.currency} 
                  selectedPeriod={selectedPeriod}
                />
              </div>
            );
          case 'customer-analytics':
            return (
              <div className="animate-enter-up stagger-9">
                <CustomerAnalyticsDashboard 
                  branchId={selectedBranch?.id}
                  customTimeRange={convertToApiTimeFrame(selectedPeriod)}
                />
              </div>
            );
          case 'customer-grouping':
            return (
              <div className="animate-enter-up stagger-9">
                <CustomerGroupingDashboard
                  selectedBrand={selectedBrand}
                  selectedBranch={selectedBranch}
                />
              </div>
            );
          case 'customer-purchase-behavior':
            return (
              <div className="animate-enter-up stagger-9">
                <CustomerPurchaseBehaviorDashboard
                  branchId={selectedBranch?.id}
                />
              </div>
            );
          case 'refine-customer':
            return (
              <div className="animate-slide-in-up stagger-4">
                <RefineCustomerDashboard 
                  branchId={selectedBranch?.id}
                />
              </div>
            );
          case 'presentation':
            return (
              <div className="animate-enter-up">
                <PresentationDashboard
                  selectedBrand={selectedBrand}
                  selectedBranch={selectedBranch}
                  selectedPeriod={selectedPeriod}
                />
              </div>
            );
          case 'custom':
            return (
              <div className="animate-enter-up stagger-10">
                <TailwindCustomDashboard 
                  selectedBrand={selectedBrand} 
                  selectedBranch={selectedBranch}
                  selectedPeriod={selectedPeriod}
                />
              </div>
            );
          case 'technical':
            return (
              <div className="animate-scale-in stagger-1">
                <TechnicalShowcaseDashboard />
              </div>
            );
          case 'product-doc':
            return (
              <div className="animate-enter-up">
                <ProductDocumentDashboard />
              </div>
            );
          case 'changelog':
          case 'release-notes':
            return (
              <div className="animate-enter-up">
                <ChangelogDashboard />
              </div>
            );
          case 'brand-overview':
            return (
              <div className="animate-enter-up stagger-11">
                <BrandOverviewDashboard
                  brandId={selectedBrand?.id}
                  timeRange={selectedPeriod}
                />
              </div>
            );
          case 'branch-comparison':
            return (
              <div className="animate-enter-up stagger-12">
                <BranchComparisonDashboard
                  brandId={selectedBrand?.id}
                  timeRange={selectedPeriod}
                />
              </div>
            );
          case 'branch-summary':
            return (
              <div className="animate-enter-up stagger-13">
                <BranchSummaryDashboard
                  brandId={selectedBrand?.id}
                  timeRange={selectedPeriod}
                />
              </div>
            );
          default:
            return (
              <div className="animate-enter-up">
                <SatisfactionDashboard 
                  branchId={selectedBranch?.id} 
                  timePeriod={selectedPeriod}
                />
              </div>
            );
        }
      }}
    </DeviceAwareLayout>
  );
};

// Main App component
function App() {

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <Routes>
          <Route path="/auth/validate-magic-token" element={<MagicTokenHandler />} />
          <Route path="/auth/env-handoff" element={<EnvHandoffHandler />} />
          <Route path="/demo-login" element={
            <AuthProvider>
              <ThemeProvider>
                <div dir="rtl">
                  <DemoLoginPage />
                </div>
              </ThemeProvider>
            </AuthProvider>
          } />
          <Route path="/dashboard/main" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="main" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/evaluation" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="evaluation" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/evaluation-growth" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="evaluation-growth" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/hr" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="hr" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/satisfaction" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="satisfaction" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/subscription" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="subscription" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/market-pulse" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="market-pulse" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/benchmark" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="benchmark" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/benchmark-indicators" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="benchmark-indicators" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/benchmark-monthly" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="benchmark-monthly" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/market-indicators" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="market-indicators" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/sales" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="sales" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/club" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="club" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/club-conditions" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="club-conditions" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/club-heatmap" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="club-heatmap" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/executive" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="executive" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/customer-analytics" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="customer-analytics" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/customer-grouping" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="customer-grouping" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/customer-purchase-behavior" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="customer-purchase-behavior" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/refine-customer" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="refine-customer" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/custom" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="custom" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/technical" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="technical" />
            </DashboardWrapper>
          } />

          <Route path="/dashboard/product-doc" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="product-doc" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/changelog" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="changelog" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/release-notes" element={
            <Navigate to="/dashboard/changelog" replace />
          } />
          <Route path="/dashboard/brand-overview" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="brand-overview" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/branch-comparison" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="branch-comparison" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/branch-summary" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="branch-summary" />
            </DashboardWrapper>
          } />
          <Route path="/dashboard/presentation" element={
            <DashboardWrapper>
              <DashboardRenderer dashboardId="presentation" />
            </DashboardWrapper>
          } />
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/dashboard/main" replace />} />
        </Routes>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
